import { ThemeSchemaResponse } from '@api/api/theme-overview';
import { PieChart } from '@design/charts/pie-chart/PieChart';
import { SkeletonLoader } from '@design/components/skeleton-loader/SkeletonLoader';
import { SectionTitle } from '@terminal/components/section-title/SectionTitle';
import { useFinancialSectorDistributions } from '@terminal/hooks/use-theme-sector-distributions/useFinancialSectorDistributions';
import { useChartTheme } from '@terminal/views/turbo-chart/hooks/use-chart-theme/useChartTheme';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import styles from './FinancialSectorDistributionSection.module.scss';

export const FinancialSectorDistributionSection = ({
  theme,
  className,
  isCompact = false,
}: {
  theme: ThemeSchemaResponse;
  className?: string;
  isCompact?: boolean;
}) => {
  const { themeName } = useChartTheme();
  const { t } = useTranslation(['widget']);

  const { data, isFetching, isError } = useFinancialSectorDistributions({
    themeId: theme.id,
  });

  if (isFetching) {
    return (
      <SkeletonLoader
        className={styles.skeletonLoaderWrapper}
        areas={[
          {
            styles: {
              width: '100%',
              height: '204px',
              borderRadius: '8px',
            },
          },
        ]}
      />
    );
  }

  if (isError || !data) {
    throw new Error('Sector Distribution: api error');
  }

  const { totalCompaniesCount, sectorDistributions } = data;

  const subtitleLabelText =
    totalCompaniesCount > 30
      ? t('widget:themeOverview.tabs.overview.topCompaniesByRankOver30', {
          count: totalCompaniesCount,
        })
      : t('widget:themeOverview.tabs.overview.topCompaniesByRankUnder30', {
          count: totalCompaniesCount,
        });

  return (
    <div
      className={clsx(styles.root, className, {
        [styles.compact]: isCompact,
      })}
      data-testid="financial-sector-distribution-section"
    >
      <div className={styles.titleWrapper}>
        <div className={styles.sectionTitle}>
          <SectionTitle
            title={t('widget:themeOverview.tabs.overview.sectorDistributions')}
            variant={isCompact ? 'small' : 'default'}
          />
        </div>
        <div className={styles.subtitleLabel}>{subtitleLabelText}</div>
      </div>
      <div className={styles.pieChartWrapper} data-pie-chart-wrapper>
        <PieChart
          theme={themeName}
          data={sectorDistributions}
          disableAnimation={!isCompact}
        />
      </div>
    </div>
  );
};
